import { createResource } from "~/lib/resourceFactory";
import {
  getAccountOverview,
  getAccountTypeOverview,
} from "./account-overview.api";

export type AccountOverviewParams = { businessId: string };
export type AccountOverviewData = Awaited<
  ReturnType<typeof getAccountOverview>
>;
export type AccountOverviewCardFeedItem = {
  type: "asset" | "equity" | "liability" | "revenue" | "expense";
  balance: number;
  delta: number | null;
  months: number[];
  monthNames: string[];
};
export type AccountTypeOverviewParams = {
  businessId: string;
  type: AccountOverviewCardFeedItem["type"];
};
export type AccountTypeOverviewData = Awaited<
  ReturnType<typeof getAccountTypeOverview>
>;

export const AccountOverviewResource = createResource<
  AccountOverviewData,
  AccountOverviewParams
>({
  key: (p) => ["account-overview", p.businessId] as const,
  fetcher: (p) => getAccountOverview({ data: { businessId: p.businessId } }),
  // Avoid suspending SSR when the query is disabled (no active business).
  placeholderData: {
    byType: {
      asset: { balance: 0, delta: null, months: [], monthNames: [] },
      equity: { balance: 0, delta: null, months: [], monthNames: [] },
      liability: { balance: 0, delta: null, months: [], monthNames: [] },
      revenue: { balance: 0, delta: null, months: [], monthNames: [] },
      expense: { balance: 0, delta: null, months: [], monthNames: [] },
    },
  },
});

export const AccountTypeOverviewResource = createResource<
  AccountTypeOverviewData,
  AccountTypeOverviewParams
>({
  key: (p) => ["account-overview", p.businessId, p.type] as const,
  fetcher: (p) =>
    getAccountTypeOverview({
      data: { businessId: p.businessId, type: p.type },
    }),
});

export function toAccountOverviewCardFeed(
  data: AccountOverviewData | undefined,
): AccountOverviewCardFeedItem[] {
  if (!data) return [];

  return ["asset", "equity", "liability"].map((type) => {
    const item = data.byType[type as keyof AccountOverviewData["byType"]];
    return {
      type: type as AccountOverviewCardFeedItem["type"],
      balance: item.balance,
      delta: item.delta,
      months: item.months,
      monthNames: item.monthNames,
    };
  });
}
